import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from '@emotion/styled';
import { VisibilityOutlined } from '@mui/icons-material';
import { uiTheme as t } from 'Components/ui';
import CourseCardV2 from 'Components/CourseCardV2/CourseCardV2';
import Modal from 'Components/Modal';
import { MexlogImageFallback } from 'Images/index';
import { s3 } from 'Services/AWS/s3';

const MAX_SIZE_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png';

const Layout = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  alignItems: 'flex-start',
});

const Zone = styled.div(({ active }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  width: '100%',
  maxWidth: '30rem',
  height: '11.625rem',
  boxSizing: 'border-box',
  border: `1.5px dashed ${active ? t.color.primary : t.color.dropBorder}`,
  borderRadius: t.radius.md,
  background: t.color.surface,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
}));

const Circle = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.875rem',
  height: '2.875rem',
  borderRadius: '50%',
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  color: t.color.primary,
  fontSize: '1.25rem',
  lineHeight: 1,
});

// Sobre el fondo de la portada, así que necesitan su propia caja legible.
const Center = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1.25rem',
  borderRadius: t.radius.sm,
  background: 'rgba(255, 255, 255, 0.88)',
});

const Main = styled.div({
  fontFamily: t.font,
  fontSize: '0.8125rem',
  color: t.color.text,
});

const Spec = styled.div({
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: '0.6875rem',
  color: t.color.muted,
});

const Filled = styled.img(({ faded }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  // El fallback es contexto, no contenido: se atenúa para que las
  // instrucciones de carga sigan siendo lo primero que se lee.
  opacity: faded ? 0.35 : 1,
}));

// Va dentro de la zona, así que su clic no debe abrir el explorador de
// archivos: el handler corta la propagación.
const PreviewButton = styled.button({
  position: 'absolute',
  top: '0.625rem',
  right: '0.625rem',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '2rem',
  padding: 0,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.xs,
  background: 'rgba(255, 255, 255, 0.92)',
  color: t.color.text,
  cursor: 'pointer',
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': {
    background: t.color.surface,
    color: t.color.primary,
  },
});

// Sobre la imagen: sin esto no hay forma de saber que la zona sigue siendo
// clicable para reemplazarla.
const Overlay = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: '0.625rem',
  textAlign: 'center',
  fontFamily: t.font,
  fontSize: '0.75rem',
  color: t.color.surface,
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0) 45%)',
});

const PreviewBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '1.5rem',
  background: t.color.pageBg,
});

const PreviewTitle = styled.span({
  fontFamily: t.font,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: t.color.titleStrong,
});

// El card real mide su ancho por la rejilla de "Mis cursos"; aquí se acota al
// mismo ancho que tiene allá para que el encuadre sea el de verdad.
const CardFrame = styled.div({
  width: '17rem',
  maxWidth: '100%',
  margin: '0 auto',
  '& > *': { width: '100%' },
});

const ErrorText = styled.div({
  marginTop: '0.375rem',
  fontFamily: t.font,
  fontSize: '0.75rem',
  color: t.color.danger,
});

/**
 * Campo de portada del curso: zona de carga y vista previa bajo demanda.
 *
 * Muestra siempre la imagen vigente —la guardada, firmada contra S3, la que el
 * usuario acaba de elegir, o el fallback que recibe un curso sin portada—. El
 * card de "Mis cursos" queda detrás del botón "Previsualizar" para no repetir
 * dos veces la misma imagen en la pantalla.
 *
 * @param {object} props
 * @param {string|object|null} props.value - Key en S3 del curso, o el archivo nuevo sin subir.
 * @param {Function} props.onFile - Recibe el archivo elegido.
 * @param {string} props.courseName - Nombre del curso para el card.
 * @param {string} [props.description] - Descripción del curso para el card.
 * @param {string} [props.categoryName] - Categoría del curso para el card.
 */
const ThumbnailField = ({ value, onFile, courseName, description, categoryName }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const file = value && typeof value === 'object' ? value.file : null;
  const storedKey = typeof value === 'string' && value ? value : null;

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: ACCEPT,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_SIZE_BYTES,
    onDrop: accepted => {
      if (accepted[0]) onFile(accepted[0]);
    },
  });

  // Imagen ya guardada: la key de S3 no se puede pintar directo, hay que firmarla.
  useEffect(() => {
    let cancelled = false;

    if (!storedKey) {
      setSignedUrl(null);
      return undefined;
    }

    s3.getSignedUrl(storedKey)
      .then(response => {
        if (!cancelled) setSignedUrl(response?.signedUrl ?? null);
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [storedKey]);

  // Preview del archivo nuevo, liberando el object URL anterior.
  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setLocalPreview(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const preview = localPreview || signedUrl;

  const rejection = fileRejections[0]?.errors?.[0];
  const rejectionMessage = (() => {
    if (!rejection) return null;
    if (rejection.code === 'file-too-large') return 'La imagen no puede pesar más de 2 MB.';
    if (rejection.code === 'file-invalid-type') return 'Solo se aceptan imágenes JPG o PNG.';
    return rejection.message;
  })();

  return (
    <>
      <Layout>
        {/* Sin portada propia se pinta el mismo fallback que verá el empleado: el
            curso nunca sale sin imagen, y así se ve cuál le tocaría. */}
        <Zone {...getRootProps()} active={isDragActive}>
          <input {...getInputProps()} />
          <Filled
            src={preview || MexlogImageFallback}
            faded={!preview}
            alt={`Portada de ${courseName || 'el curso'}`}
          />
          <PreviewButton
            type="button"
            aria-label="Previsualizar"
            title="Previsualizar"
            onClick={event => {
              event.stopPropagation();
              setPreviewOpen(true);
            }}
          >
            <VisibilityOutlined fontSize="small" />
          </PreviewButton>
          {preview ? (
            <Overlay>{isDragActive ? 'Suelta para reemplazarla' : 'Haz clic o arrastra para reemplazarla'}</Overlay>
          ) : (
            <Center>
              <Circle aria-hidden="true">↑</Circle>
              <Main>{isDragActive ? 'Suelta tu imagen aquí' : 'Arrastra tu imagen aquí'}</Main>
              <Spec>1280×720 · máx 2 MB</Spec>
            </Center>
          )}
        </Zone>

        {rejectionMessage && <ErrorText>{rejectionMessage}</ErrorText>}
      </Layout>

      <Modal
        open={previewOpen}
        setOpen={setPreviewOpen}
        size="xs"
        event={() => setPreviewOpen(false)}
        Children={
          <PreviewBody>
            <PreviewTitle>Así se verá en Mis cursos</PreviewTitle>
            <CardFrame>
              <CourseCardV2
                name={courseName || 'Nombre del curso'}
                thumbnailUrl={preview}
                description={description || null}
                category={categoryName ? { name: categoryName } : null}
                onClick={() => setPreviewOpen(false)}
              />
            </CardFrame>
          </PreviewBody>
        }
      />
    </>
  );
};

export default ThumbnailField;
